import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { CollectionsShell } from './CollectionsShell';
import type { ArTabId } from './CollectionsShell';
import { ArInsurerDrawer } from './ArInsurerDrawer';
import {
  AR_BALANCE_BREAKDOWNS,
  AR_FUNNEL,
  AR_FUNNEL_NOTE,
  AR_NUMBERS,
  AR_SIDE_TILES,
} from '../../ar/mockData';
import type { ArBreakdown, ArFunnelRow, ArSideTile } from '../../ar/types';

export interface ArBalancesViewProps {
  onSelectTab?: (tab: ArTabId) => void;
  onOpenSettings?: () => void;
  onCreateLink?: () => void;
  onNewFollowUp?: () => void;
  /** Start a follow-up from a breakdown row, carrying the description (§2.1). */
  onStartFollowUp?: (description: string) => void;
}

/**
 * Collections › Balances (amendment 28 §2.1, board `AR1Balances`).
 *
 * The subtraction funnel — from everything owed down to what ELVA can follow up —
 * with the three side tiles, and three breakdowns (by age, by size, what for),
 * each row starting a follow-up. The numbers are `AR_NUMBERS` and they add up.
 */
export function ArBalancesView({ onSelectTab, onOpenSettings, onCreateLink, onNewFollowUp, onStartFollowUp }: ArBalancesViewProps) {
  const [seeWho, setSeeWho] = useState(false);
  const [byAge, bySize, whatFor] = AR_BALANCE_BREAKDOWNS;
  const eyebrow = `${String(AR_NUMBERS.followablePatients)} can be followed up · ${AR_NUMBERS.followableValue} · ${String(AR_NUMBERS.inFollowUp)} in a follow-up`;

  return (
    <CollectionsShell
      active="balances"
      eyebrow={eyebrow}
      onSelectTab={onSelectTab}
      onOpenSettings={onOpenSettings}
      onCreateLink={onCreateLink}
      onNewFollowUp={onNewFollowUp}
    >
      <div className="mt-5 grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4 max-[1000px]:grid-cols-1">
        {/* the funnel */}
        <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
          <div className="pc-eyebrow text-[var(--text-muted)]">From everything owed to what ELVA can follow up</div>
          <div className="mt-3 overflow-hidden rounded-[11px] border border-[var(--border-default)]">
            <div className="grid grid-cols-[minmax(0,1fr)_72px_92px] items-center gap-x-2 bg-[#F4F6FB] px-3 py-2 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
              <span />
              <span className="text-right">Patients</span>
              <span className="text-right">Value</span>
            </div>
            {AR_FUNNEL.map((r) => (
              <FunnelRow key={r.key} r={r} onSeeWho={() => { setSeeWho(true); }} />
            ))}
          </div>
          <div className="mt-3 rounded-[10px] bg-[var(--surface-sunken)] px-3 py-2.5 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">
            <span className="font-semibold text-[var(--text-primary)]">{AR_FUNNEL_NOTE.waitingLead}</span>
            {AR_FUNNEL_NOTE.waitingBody}
            <span className="font-semibold text-[var(--text-primary)]">{AR_FUNNEL_NOTE.nowLead}</span>
            {AR_FUNNEL_NOTE.nowBody}
          </div>
        </div>

        {/* side tiles */}
        <div className="flex flex-col gap-3">
          {AR_SIDE_TILES.map((t) => (
            <SideTile key={t.eyebrow} t={t} onSeeWho={() => { setSeeWho(true); }} />
          ))}
        </div>
      </div>

      {/* 01 by age — full width */}
      {byAge !== undefined ? (
        <div className="mt-4"><BreakdownCard b={byAge} wide onStartFollowUp={onStartFollowUp} /></div>
      ) : null}

      {/* 02 by size · 03 what for */}
      <div className="mt-4 grid grid-cols-2 gap-4 max-[1000px]:grid-cols-1">
        {bySize !== undefined ? <BreakdownCard b={bySize} onStartFollowUp={onStartFollowUp} /> : null}
        {whatFor !== undefined ? <BreakdownCard b={whatFor} onStartFollowUp={onStartFollowUp} /> : null}
      </div>

      <ArInsurerDrawer open={seeWho} onClose={() => { setSeeWho(false); }} />
    </CollectionsShell>
  );
}

function FunnelRow({ r, onSeeWho }: { r: ArFunnelRow; onSeeWho: () => void }) {
  const total = r.kind === 'total';
  const gross = r.kind === 'gross';
  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_72px_92px] items-center gap-x-2 border-t border-[var(--border-default)] px-3 py-2.5 first:border-0',
        total ? 'bg-[var(--brand-indigo-tint)]' : '',
      )}
    >
      <div className="min-w-0">
        <div className={cn('text-[12.5px]', total || gross ? 'font-bold text-[var(--text-primary)]' : 'font-semibold text-[var(--text-secondary)]')}>
          {r.label}
        </div>
        {r.note !== undefined ? <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">{r.note}</div> : null}
        {r.seeWho === true ? (
          <button type="button" onClick={onSeeWho} className="mt-0.5 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">See who</button>
        ) : null}
      </div>
      <div className="text-right font-[var(--font-mono)] text-[12.5px] font-bold text-[var(--text-primary)]">{r.patients}</div>
      <div className={cn('text-right font-[var(--font-mono)] text-[12.5px] font-bold', total ? 'text-[var(--money-green)]' : gross ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>
        {r.value}
      </div>
    </div>
  );
}

function SideTile({ t, onSeeWho }: { t: ArSideTile; onSeeWho: () => void }) {
  return (
    <div className="rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5 shadow-[var(--shadow-card)]">
      <div className="pc-eyebrow text-[var(--text-muted)]">{t.eyebrow}</div>
      <div className="mt-1 font-[var(--font-mono)] text-[22px] font-bold text-[var(--text-primary)]">{t.value}</div>
      <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">{t.sub}</div>
      {t.seeWho === true ? (
        <button type="button" onClick={onSeeWho} className="mt-1.5 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">See who →</button>
      ) : null}
    </div>
  );
}

function BreakdownCard({ b, wide, onStartFollowUp }: { b: ArBreakdown; wide?: boolean | undefined; onStartFollowUp?: ((d: string) => void) | undefined }) {
  return (
    <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]">
      <div className="flex items-baseline gap-2">
        <span className="font-[var(--font-mono)] text-[12px] font-bold text-[var(--brand-indigo)]">{b.n}</span>
        <span className="text-[14px] font-bold text-[var(--text-primary)]">{b.title}</span>
        {b.sub !== undefined ? <span className="text-[11.5px] text-[var(--text-muted)]">{b.sub}</span> : null}
      </div>
      <div className="mt-3 overflow-hidden rounded-[10px] border border-[var(--border-default)]">
        <div className={cn('grid items-center gap-x-3 bg-[#F4F6FB] px-3 py-2 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]', wide === true ? 'grid-cols-[minmax(0,1fr)_64px_84px_minmax(0,1fr)_120px]' : 'grid-cols-[minmax(0,1fr)_56px_78px_120px]')}>
          <span>{b.colLabel}</span>
          <span className="text-right">Patients</span>
          <span className="text-right">Value</span>
          {wide === true ? <span /> : null}
          <span />
        </div>
        {b.rows.map((row) => (
          <div key={row.key} className={cn('grid items-center gap-x-3 border-t border-[var(--border-default)] px-3 py-2.5 text-[12.5px]', wide === true ? 'grid-cols-[minmax(0,1fr)_64px_84px_minmax(0,1fr)_120px]' : 'grid-cols-[minmax(0,1fr)_56px_78px_120px]')}>
            <span className="font-semibold text-[var(--text-primary)]">{row.label}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{row.patients}</span>
            <span className="text-right font-[var(--font-mono)] font-bold text-[var(--text-secondary)]">{row.value}</span>
            {wide === true ? <span className="text-[11.5px] text-[var(--text-muted)]">{row.tag ?? ''}</span> : null}
            <span className="flex items-center justify-end gap-2">
              {wide !== true && row.tag !== undefined ? <span className="text-[10.5px] font-semibold text-[var(--text-muted)]">{row.tag}</span> : null}
              <button type="button" onClick={() => { onStartFollowUp?.(row.followUpDescription); }} className="whitespace-nowrap text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
                Start a follow-up →
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
